import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Salad, Star, RefreshCw, Check, Heart, Flame, Drumstick, Leaf, Info, Sparkles, Settings, Truck, MapPin, Clock, DollarSign, ShoppingCart, X, Store, Sprout, Award, Apple, Pill, Stethoscope } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { showSupportMessage, showCelebration } from '@/utils/emotionalSupport';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CheckoutModal from '@/components/CheckoutModal';
import NeedHelpButton from '@/components/NeedHelpButton';
import AppFooter from '@/components/AppFooter';
import MealPreferencesModal from '@/components/MealPreferencesModal';
import MealSwapModal from '@/components/MealSwapModal';
import QuickFeedback from '@/components/QuickFeedback';
import { getMealOriginLabel, getMealOriginBadgeStyle } from '@/utils/culturalOrigins';
import { useLanguage } from '@/contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Cultural Meal Inspiration — lightweight starter dataset (Phase 1.0 / MVP).
 * -----------------------------------------------------------------------------
 * Educational, wellness-focused, culturally inclusive. Strictly NO commerce,
 * NO ordering, NO delivery, NO monetization. Each entry is a small JSON
 * object so this can be safely extended without touching the rendering code.
 *
 * Schema (intentionally minimal — easy to grow in Phase 1.1):
 *   - name:        Display name of the dish
 *   - origin:      Country
 *   - tribe:       Optional ethnic/regional association (e.g. Yoruba, Igbo)
 *   - region:      Optional broader region (e.g. West Africa)
 *   - description: One-line educational summary
 *   - tags:        Wellness tags (subset of WELLNESS_TAGS below)
 *   - ingredients: Short list of common ingredients
 *   - note:        Optional preparation / cultural / caution note
 *
 * Curation rules followed:
 *   - Names spelled per common cultural usage
 *   - No stereotypes, no oversimplification
 *   - Health tags are advisory, not prescriptive
 *   - "Coming soon" placeholders for under-represented regions so users
 *     see the inclusivity intent without us shipping thin data
 */
const WELLNESS_TAGS = ['High-fiber', 'Low-sugar', 'Low-salt', 'Heart-friendly', 'Protein-rich', 'Energy-support', 'Pregnancy-friendly', 'Child-friendly', 'Weight-support'];

/**
 * Continent list — top-level entry point for user-controlled discovery.
 * `hasData: false` marks continents with no starter meals yet so the UI can
 * clearly say "coming soon" instead of returning an empty grid. Order is
 * deliberate: data-rich continents first so first-time users see something
 * actionable; placeholder continents listed alphabetically.
 */
const CONTINENTS = [
  { name: 'Africa',        hasData: true  },
  { name: 'Asia',          hasData: false },
  { name: 'Caribbean',     hasData: false },
  { name: 'Europe',        hasData: false },
  { name: 'Latin America', hasData: false },
  { name: 'Middle East',   hasData: false },
  { name: 'North America', hasData: false },
];

const CULTURAL_MEAL_INSPIRATION = [
  // ---- Nigeria — Yoruba ----
  {
    name: 'Amala & Ewedu',
    continent: 'Africa', origin: 'Nigeria', tribe: 'Yoruba', region: 'West Africa',
    description: 'Yam-flour swallow paired with jute-leaf soup — a comforting, fibre-rich classic.',
    tags: ['High-fiber', 'Heart-friendly', 'Low-sugar'],
    ingredients: ['Yam flour', 'Jute leaves (ewedu)', 'Locust beans (iru)'],
    note: 'Lighter on palm oil keeps it heart-friendly.',
  },
  // ---- Nigeria — Igbo ----
  {
    name: 'Oha Soup',
    continent: 'Africa', origin: 'Nigeria', tribe: 'Igbo', region: 'West Africa',
    description: 'Earthy soup made with oha leaves and cocoyam paste — rich in greens and minerals.',
    tags: ['High-fiber', 'Heart-friendly', 'Low-sugar'],
    ingredients: ['Oha leaves', 'Cocoyam', 'Stockfish', 'Uziza seeds'],
    note: 'Pair with a small portion of swallow for balanced energy.',
  },
  // ---- Nigeria — Hausa / Fulani ----
  {
    name: 'Tuwo Shinkafa',
    continent: 'Africa', origin: 'Nigeria', tribe: 'Hausa/Fulani', region: 'West Africa',
    description: 'Soft rice cake traditionally enjoyed with miyan kuka or vegetable soups.',
    tags: ['Energy-support', 'Child-friendly'],
    ingredients: ['Short-grain rice', 'Water', 'Vegetable soup of choice'],
    note: 'Whole-grain rice variants add fibre without changing the experience.',
  },
  // ---- Nigeria — South-South ----
  {
    name: 'Edikang Ikong',
    continent: 'Africa', origin: 'Nigeria', tribe: 'Efik/Ibibio', region: 'South-South Nigeria',
    description: 'Generous greens-and-pumpkin-leaf soup — a powerhouse of vitamins and fibre.',
    tags: ['High-fiber', 'Heart-friendly', 'Protein-rich', 'Pregnancy-friendly'],
    ingredients: ['Ugu (pumpkin) leaves', 'Waterleaf', 'Periwinkle', 'Smoked fish'],
    note: 'Keep palm oil modest to preserve the dish\'s natural lightness.',
  },
  // ---- Ghana ----
  {
    name: 'Waakye',
    continent: 'Africa', origin: 'Ghana', region: 'West Africa',
    description: 'Rice and beans cooked together — slow-release energy with built-in protein.',
    tags: ['Energy-support', 'Protein-rich', 'High-fiber', 'Child-friendly'],
    ingredients: ['Rice', 'Cowpeas/black-eyed peas', 'Sorghum leaves'],
    note: 'Pair with shito sparingly to keep sodium in check.',
  },
  // ---- Zimbabwe ----
  {
    name: 'Sadza with Muriwo',
    continent: 'Africa', origin: 'Zimbabwe', tribe: 'Shona / Ndebele', region: 'Southern Africa',
    description: 'Maize-meal staple served with leafy greens — simple, grounding, nourishing.',
    tags: ['High-fiber', 'Low-sugar', 'Low-salt', 'Weight-support'],
    ingredients: ['Maize meal', 'Covo / rape leaves', 'Tomato', 'Onion'],
    note: 'Greens cooked briefly retain more nutrients.',
  },
  // ---- Zambia ----
  {
    name: 'Nshima with Ifisashi',
    continent: 'Africa', origin: 'Zambia', tribe: 'Bemba / Tonga', region: 'Southern Africa',
    description: 'Maize-meal staple paired with peanut-and-greens stew — protein + greens in one bowl.',
    tags: ['Protein-rich', 'High-fiber', 'Pregnancy-friendly'],
    ingredients: ['Maize meal', 'Groundnut paste', 'Pumpkin or rape leaves'],
    note: 'Groundnut paste adds healthy fats — keep portions moderate.',
  },
];

const Nutrition = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('meal-builder');
  
  // Meal options state
  const [breakfastOptions, setBreakfastOptions] = useState([]);
  const [lunchOptions, setLunchOptions] = useState([]);
  const [dinnerOptions, setDinnerOptions] = useState([]);
  const [snacksOptions, setSnacksOptions] = useState([]);
  
  // Selected meals state
  const [selectedMeals, setSelectedMeals] = useState({
    breakfast: null,
    lunch: null,
    dinner: null,
    snacks: null
  });
  
  // Favorites state
  const [favorites, setFavorites] = useState({
    breakfast: [],
    lunch: [],
    dinner: [],
    snacks: []
  });
  const [showFavorites, setShowFavorites] = useState(false);
  
  // Loading states
  const [swapping, setSwapping] = useState({});
  
  // Food Preferences state
  const [foodCategories, setFoodCategories] = useState([]);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Cultural Meal Inspiration — explicit user-controlled discovery.
  // Cascade: continent → country → tribe → (optional) wellness tag.
  // Meals stay HIDDEN until the user picks something OR taps Browse All —
  // intentional design choice to avoid feeling like a recommendation engine.
  const [activeMealTag, setActiveMealTag] = useState(null);
  const [selectedContinent, setSelectedContinent] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedTribe, setSelectedTribe] = useState(null);
  const [browseAllMeals, setBrowseAllMeals] = useState(false);
  const [userPreferences, setUserPreferences] = useState({
    cuisines: [],
    dietary: [],
    allergies: [],
    dislikes: []
  });

  // Meal swap state
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapMealType, setSwapMealType] = useState(null);
  const [swapMealCurrent, setSwapMealCurrent] = useState(null);

  // Delivery feature state
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [userZipCode, setUserZipCode] = useState('');
  const [availableVendors, setAvailableVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [deliveryCart, setDeliveryCart] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [recentOrder, setRecentOrder] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);
  
  // Vendor selection for specific meal
  const [showVendorSelectionModal, setShowVendorSelectionModal] = useState(false);
  const [selectedMealForOrder, setSelectedMealForOrder] = useState(null);
  const [vendorsForMeal, setVendorsForMeal] = useState([]);
  
  // Checkout state
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  const availableCuisines = [
    { value: 'west_african', label: 'West African', description: 'Nigerian, Ghanaian, Senegalese' },
    { value: 'east_african', label: 'East African', description: 'Kenyan, Ethiopian, Tanzanian' },
    { value: 'caribbean', label: 'Caribbean', description: 'Jamaican, Trinidadian' },
    { value: 'western', label: 'Western', description: 'American, European' },
    { value: 'south_asian', label: 'South Asian', description: 'Indian, Pakistani' },
    { value: 'east_asian', label: 'East Asian', description: 'Chinese, Japanese, Korean' },
    { value: 'middle_eastern', label: 'Middle Eastern', description: 'Lebanese, Syrian' },
    { value: 'mediterranean', label: 'Mediterranean', description: 'Greek, Italian' },
  ];

  // ---------------------------------------------------------------------
  // Postcode / ZIP normalisation + validation.
  //
  // We accept any region's format (US 5-digit ZIP, UK postcode like
  // "MK10 9XX" / "SW1A 1AA" / "M1 1AE", Canadian, etc.) by relaxing the
  // previous "exactly 5 characters" rule. The backend just does a string
  // membership check against vendor service_areas, so passing a richer
  // postcode is safe — it'll either match or fall through to the
  // ingredient-suggestion fallback.
  //
  //   normalisePostcode("  mk10 9xx ")  → "MK10 9XX"
  //   normalisePostcode("sw1a  1aa")    → "SW1A 1AA"
  //   normalisePostcode("10001")        → "10001"
  // ---------------------------------------------------------------------
  const normalisePostcode = (raw) =>
    (raw || '').trim().toUpperCase().replace(/\s+/g, ' ');

  // Considered "complete enough to search" when we have at least 3
  // alphanumerics after normalisation. Partial inputs like "MK10" still
  // pass this gate so the user gets cultural-ingredient guidance — they
  // just see a soft hint suggesting the full postcode for better matches.
  const isPostcodeSearchable = (raw) => {
    const cleaned = normalisePostcode(raw).replace(/\s/g, '');
    return cleaned.length >= 3;
  };

  // Considered "looks like a full postcode" — a quick heuristic to
  // decide whether to show the "please enter full postcode for more
  // accurate results" hint. UK full postcodes have a space and at least
  // 5 alphanumerics; US ZIPs are exactly 5 digits.
  const isPostcodeComplete = (raw) => {
    const norm = normalisePostcode(raw);
    if (/^\d{5}$/.test(norm)) return true; // US ZIP
    if (/^[A-Z]{1,2}\d[A-Z\d]?\s\d[A-Z]{2}$/.test(norm)) return true; // UK
    return false;
  };

  // Lightweight, hand-curated culturally-relevant sourcing hints. Used by the
  // "Local Ingredient Availability" panel to give actionable guidance even
  // when no certified delivery vendor serves the user's ZIP. No external
  // APIs, no DB lookups — just realistic prompts a user can act on locally.
  const CULTURAL_INGREDIENT_HINTS = {
    west_african: {
      staples: 'amaranth (efo), egusi, ogbono, plantain, brown jollof rice',
      whereToFind: 'West-African groceries, halal markets, larger farmers\' markets',
    },
    east_african: {
      staples: 'teff flour, sukuma wiki (collard greens), millet, sorghum, mukimo veg mix',
      whereToFind: 'East-African / Ethiopian groceries, halal markets, organic co-ops',
    },
    caribbean: {
      staples: 'callaloo, ackee, scotch bonnet, breadfruit, brown rice',
      whereToFind: 'Caribbean groceries, West-Indian markets, larger Latin supermarkets',
    },
    western: {
      staples: 'leafy greens, whole-grain bread, lean poultry, plain Greek yogurt, berries',
      whereToFind: 'Local farmers\' markets, organic co-ops, neighborhood butchers',
    },
    south_asian: {
      staples: 'moong dal, brown basmati, paneer, ghee, fenugreek, turmeric',
      whereToFind: 'Indian / Pakistani groceries, halal markets, spice stores',
    },
    east_asian: {
      staples: 'tofu, edamame, brown rice, bok choy, daikon, miso, seaweed',
      whereToFind: 'East-Asian groceries, Korean / Japanese markets, organic produce stalls',
    },
    middle_eastern: {
      staples: 'bulgur, chickpeas, tahini, freekeh, halloumi, sumac',
      whereToFind: 'Middle-Eastern groceries, Mediterranean markets, halal butchers',
    },
    mediterranean: {
      staples: 'olive oil, lentils, farro, feta, sardines, fresh herbs',
      whereToFind: 'Mediterranean delis, farmers\' markets, organic co-ops',
    },
  };

  // Universal wellness foods — informational, region-agnostic. Surfaces
  // calm, evidence-aligned everyday choices alongside the cultural
  // suggestions.
  const POPULAR_WELLNESS_FOODS = [
    'Leafy greens (spinach, kale, collards)',
    'Whole grains (oats, brown rice, quinoa)',
    'Fresh fruits in season',
    'Legumes (beans, lentils, chickpeas)',
    'Nuts and seeds (unsalted)',
    'Low-sugar snacks (fresh fruit, plain yogurt)',
  ];

  // Lightweight healthy-swap ideas. Educational only — no products,
  // no brands, no marketplace.
  const HEALTHY_SWAP_IDEAS = [
    { from: 'Sugary drinks',     to: 'Infused water, herbal tea' },
    { from: 'White bread',       to: 'Whole-grain or seeded bread' },
    { from: 'Fried snacks',      to: 'Roasted nuts or air-popped popcorn' },
    { from: 'Processed meals',   to: 'Fresh ingredients you cook at home' },
    { from: 'Sweetened cereal',  to: 'Oats with fruit and nuts' },
  ];

  // Per-cuisine "where to look in your neighbourhood" guidance for the
  // Local Wellness Environment section. Discovery-only language — no
  // partnership claims, no certifications, no live ordering.
  const LOCAL_WELLNESS_ENVIRONMENTS = {
    west_african: ['West-African groceries', 'Halal markets', 'Farmers\' markets with fresh produce'],
    east_african: ['East-African / Ethiopian groceries', 'Halal markets', 'Organic produce co-ops'],
    caribbean:    ['Caribbean groceries', 'West-Indian markets', 'Latin supermarkets with fresh produce'],
    western:      ['Local farmers\' markets', 'Organic co-ops', 'Whole-foods grocery aisles'],
    south_asian:  ['Indian / Pakistani groceries', 'Halal butchers', 'Spice and bulk-grain stores'],
    east_asian:   ['East-Asian groceries', 'Korean / Japanese markets', 'Fresh-produce aisles in larger supermarkets'],
    middle_eastern: ['Middle-Eastern groceries', 'Mediterranean markets', 'Halal butchers'],
    mediterranean: ['Mediterranean delis', 'Farmers\' markets', 'Organic-produce sections'],
  };

  // Healthy Stores & Wellness Markets — INFORMATIONAL ONLY.
  // Static, culturally inclusive list of store/market *types* a user
  // can look for in their neighbourhood. No commerce, no live
  // inventory, no booking, no pricing, no delivery. Pure discovery.
  // `key` maps to translation entries under nutritionDeep.healthyStores.*
  // so type/description/tags can localise. English values stay as fallback.
  const HEALTHY_STORE_TYPES = [
    { key: 'organic',       type: 'Organic grocery stores',           description: 'Fresh, pesticide-free produce and minimally processed pantry staples.',                  tags: ['Whole-foods', 'Fresh produce'] },
    { key: 'farmers',       type: "Farmers' markets",                 description: 'Locally grown seasonal fruit, vegetables, and traditional produce direct from growers.',  tags: ['Local', 'Seasonal'] },
    { key: 'african',       type: 'African fresh produce stores',     description: 'Yams, plantains, leafy greens, palm nut and West / East African staples.',               tags: ['Cultural', 'Fresh produce'] },
    { key: 'caribbean',     type: 'Caribbean natural food markets',   description: 'Callaloo, cassava, scotch bonnet, ackee, breadfruit and island staples.',                 tags: ['Cultural', 'Whole-foods'] },
    { key: 'asian',         type: 'Asian vegetable supermarkets',     description: 'Bok choy, daikon, lotus root, tofu, fresh herbs and pan-Asian staples.',                 tags: ['Cultural', 'Fresh produce'] },
    { key: 'halal',         type: 'Halal healthy food markets',       description: 'Halal-certified lean proteins, fresh produce and traditional spices.',                   tags: ['Halal', 'Cultural'] },
    { key: 'mediterranean', type: 'Mediterranean whole-food markets', description: 'Olive oil, legumes, fresh herbs, whole grains and Mediterranean staples.',               tags: ['Cultural', 'Whole-foods'] },
    { key: 'wellness',      type: 'Wellness-focused grocery shops',   description: 'Curated whole foods, plant-based options and clean-label pantry items.',                 tags: ['Whole-foods', 'Plant-based'] },
  ];

  // General healthy-shopping guidance. Universal, educational, no
  // brand or product references.
  const HEALTHY_SHOPPING_TIPS = [
    'Shop the perimeter first — fresh produce, lean proteins and dairy alternatives usually live around the edges.',
    'Favour whole, single-ingredient foods over heavily processed items.',
    'Choose seasonal local produce — usually fresher, more affordable and more nutrient-dense.',
    'Ask vendors about origin and growing methods — many will happily share.',
  ];

  // Resolve the cuisines the user actually cares about — fallback chain so
  // we always have something useful to render in the ingredient panel.
  const getActiveCuisines = () => {
    const fromPrefs = (userPreferences?.cuisines || []).filter(Boolean);
    if (fromPrefs.length) return fromPrefs;
    const fromSelected = (selectedCategories || []).filter(Boolean);
    if (fromSelected.length) return fromSelected;
    return ['western']; // safe default; broadly applicable
  };

  useEffect(() => {
    fetchMealOptions();
    fetchFavorites();
    fetchUserPreferences();
  }, []);

  const fetchMealOptions = async () => {
    setLoading(true);
    try {
      const [breakfastRes, lunchRes, dinnerRes, snacksRes] = await Promise.all([
        axios.get(`${API}/nutrition/meal-options?meal_type=breakfast&count=5`, { withCredentials: true }),
        axios.get(`${API}/nutrition/meal-options?meal_type=lunch&count=5`, { withCredentials: true }),
        axios.get(`${API}/nutrition/meal-options?meal_type=dinner&count=5`, { withCredentials: true }),
        axios.get(`${API}/nutrition/meal-options?meal_type=snacks&count=5`, { withCredentials: true })
      ]);
      
      setBreakfastOptions(breakfastRes.data.options || []);
      setLunchOptions(lunchRes.data.options || []);
      setDinnerOptions(dinnerRes.data.options || []);
      setSnacksOptions(snacksRes.data.options || []);
    } catch (error) {
      console.error('Error fetching meal options:', error);
      toast.error(t('nutritionDeep.mealOptionsLoadFail'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/nutrition/favorites`, { withCredentials: true });
      setFavorites(res.data.favorites || {});
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const categories = res.data.selected_food_categories || [];
      setFoodCategories(categories);
      setSelectedCategories(categories);
      
      // If no preferences set, show modal once per session/device
      // (dismissal persists in localStorage so the prompt does not
      // re-appear on every page reload — addresses QA feedback that
      // the modal was blocking Healthy Local Discovery on each load).
      const prefsPromptDismissed = localStorage.getItem('nutritionPrefsPromptDismissed') === '1';
      if (categories.length === 0 && !prefsPromptDismissed) {
        setShowPreferencesModal(true);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const handleSavePreferences = async () => {
    try {
      await axios.put(`${API}/auth/profile`, {
        selected_food_categories: selectedCategories
      }, { withCredentials: true });
      
      // Also save to localStorage for meal filtering
      const prefs = {
        cuisines: selectedCategories,
        dietary: userPreferences.dietary || [],
        allergies: userPreferences.allergies || [],
        dislikes: userPreferences.dislikes || []
      };
      localStorage.setItem('mealPreferences', JSON.stringify(prefs));
      
      setFoodCategories(selectedCategories);
      setShowPreferencesModal(false);
      toast.success(t('nutritionDeep.prefsSaved'));
      
      // Refresh meal options with new preferences
      fetchMealOptions();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(t('nutritionDeep.prefsSaveFail'));
    }
  };

  const handleSwapMeal = (mealType, currentMeal) => {
    setSwapMealType(mealType);
    setSwapMealCurrent(currentMeal);
    setShowSwapModal(true);
  };

  const handleSwapComplete = (newMeal) => {
    setSelectedMeals(prev => ({
      ...prev,
      [swapMealType]: newMeal
    }));
    setShowSwapModal(false);
    toast.success(`✅ ${swapMealType.charAt(0).toUpperCase() + swapMealType.slice(1)} swapped successfully!`);
  };

  const handlePreferencesSave = (newPreferences) => {
    setUserPreferences(newPreferences);
    setSelectedCategories(newPreferences.cuisines || []);
    localStorage.setItem('mealPreferences', JSON.stringify(newPreferences));
    setShowPreferencesModal(false);
    toast.success('🎉 Meal preferences updated! Refreshing recommendations...');
    fetchMealOptions();
  };

  const toggleCategorySelection = (categoryValue) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryValue)) {
        return prev.filter(c => c !== categoryValue);
      } else {
        return [...prev, categoryValue];
      }
    });
  };

  const handleSelectMeal = async (mealType, meal) => {
    setSelectedMeals(prev => ({
      ...prev,
      [mealType]: meal
    }));
    
    // Track selection and get support message
    try {
      const response = await axios.post(`${API}/nutrition/track-interaction`, {
        meal_name: meal.name,
        meal_type: mealType,
        cuisine: meal.cuisine || '',
        interaction_type: 'selected',
        meal_data: meal
      }, { withCredentials: true });
      
      // Show emotional support message
      if (response.data.support_message) {
        showSupportMessage(response.data.support_message, 'success');
      } else {
        toast.success(`${meal.name} selected for ${mealType}!`);
      }
    } catch (error) {
      console.error('Error tracking selection:', error);
      toast.success(`${meal.name} selected for ${mealType}!`);
    }
  };

  const handleSwapMeals = async (mealType) => {
    setSwapping(prev => ({ ...prev, [mealType]: true }));
    
    try {
      // Get current options for this meal type to exclude them
      const currentOptions = {
        breakfast: breakfastOptions,
        lunch: lunchOptions,
        dinner: dinnerOptions,
        snacks: snacksOptions
      }[mealType];
      
      const excludeNames = currentOptions.map(m => m.name);
      
      // Track swap as implicit skip for all current options
      for (const meal of currentOptions) {
        try {
          await axios.post(`${API}/nutrition/track-interaction`, {
            meal_name: meal.name,
            meal_type: mealType,
            cuisine: meal.cuisine || '',
            interaction_type: 'skipped',
            meal_data: meal
          }, { withCredentials: true });
        } catch (error) {
          console.error('Error tracking skip:', error);
        }
      }
      
      const res = await axios.post(`${API}/nutrition/swap-meal`, {
        meal_type: mealType,
        exclude_meals: excludeNames,
        count: 5
      }, { withCredentials: true });
      
      const newOptions = res.data.options || [];
      
      // Update the appropriate options state
      if (mealType === 'breakfast') setBreakfastOptions(newOptions);
      else if (mealType === 'lunch') setLunchOptions(newOptions);
      else if (mealType === 'dinner') setDinnerOptions(newOptions);
      else if (mealType === 'snacks') setSnacksOptions(newOptions);
      
      toast.success(`New ${mealType} options loaded!`);
    } catch (error) {
      console.error('Error swapping meals:', error);
      toast.error(t('nutritionDeep.newOptionsFail'));
    } finally {
      setSwapping(prev => ({ ...prev, [mealType]: false }));
    }
  };


  // ============= DELIVERY FEATURE FUNCTIONS =============

  const fetchVendors = async (zipCode) => {
    if (!isPostcodeSearchable(zipCode)) {
      toast.error(t('nutritionDeep.invalidZip'));
      return;
    }

    setLoadingVendors(true);
    try {
      // encodeURIComponent so UK postcodes with a space (e.g. "MK10 9XX")
      // travel safely through the URL.
      const res = await axios.get(`${API}/delivery/vendors?zip_code=${encodeURIComponent(zipCode)}`, {
        withCredentials: true
      });

      if (res.data.success) {
        setAvailableVendors(res.data.vendors || []);
        if (res.data.vendors.length > 0) {
          toast.success(`Found ${res.data.vendors.length} healthy meal vendors in your area!`);
        } else {
          toast.info(t('nutritionDeep.noVendorsArea'));
        }
      } else {
        setAvailableVendors([]);
        toast.info(res.data.message || 'No vendors available in this area');
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error(t('nutritionDeep.vendorsFetchFail'));
      setAvailableVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const handleDeliveryToggle = (checked) => {
    setDeliveryEnabled(checked);
    if (!checked) {
      // Reset delivery state when disabling
      setSelectedVendor(null);
      setDeliveryCart([]);
    }
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setDeliveryCart([]); // Clear cart when switching vendors
    
    // Fetch deliverable meals with pricing for this vendor
    fetchDeliverableMeals(vendor.id);
    
    toast.success(`Selected ${vendor.name} for delivery`);
  };

  const fetchDeliverableMeals = async (vendorId) => {
    try {
      // Fetch deliverable meals for each meal type and update with pricing
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snacks'];
      
      for (const mealType of mealTypes) {
        const res = await axios.get(
          `${API}/delivery/available-meals?vendor_id=${vendorId}&meal_type=${mealType}`,
          { withCredentials: true }
        );

        if (res.data.success && res.data.meals) {
          // Update the meal options with pricing
          const mealsWithPricing = res.data.meals;
          
          if (mealType === 'breakfast') setBreakfastOptions(mealsWithPricing);
          else if (mealType === 'lunch') setLunchOptions(mealsWithPricing);
          else if (mealType === 'dinner') setDinnerOptions(mealsWithPricing);
          else if (mealType === 'snacks') setSnacksOptions(mealsWithPricing);
        }
      }
    } catch (error) {
      console.error('Error fetching deliverable meals:', error);
      // Don't show error toast, fall back to regular meal recommendations
    }
  };

  const addToCart = (meal, mealType) => {
    if (!selectedVendor) {
      toast.error(t('nutritionDeep.selectVendorFirst'));
      return;
    }

    const mealPrice = meal.price || 9.99;  // Default price if not set
    const cartItem = {
      ...meal,
      meal_type: mealType,
      vendor_id: selectedVendor.id,
      vendor_name: selectedVendor.name,
      quantity: 1,
      price: mealPrice,
      subtotal: mealPrice * 1
    };

    setDeliveryCart(prev => {
      // Check if meal already in cart
      const existing = prev.find(item => item.name === meal.name);
      if (existing) {
        return prev.map(item =>
          item.name === meal.name
            ? { ...item, quantity: item.quantity + 1, subtotal: item.price * (item.quantity + 1) }
            : item
        );
      }
      return [...prev, cartItem];
    });

    toast.success(`Added ${meal.name} to cart`);
    showSupportMessage('cart_add');
  };

  const removeFromCart = (mealName) => {
    setDeliveryCart(prev => prev.filter(item => item.name !== mealName));
    toast.success(t('nutritionDeep.removedFromCart'));
  };

  const updateCartQuantity = (mealName, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(mealName);
      return;
    }

    setDeliveryCart(prev =>
      prev.map(item =>
        item.name === mealName
          ? { ...item, quantity: newQuantity, subtotal: item.price * newQuantity }
          : item
      )
    );
  };

  const calculateCartTotal = () => {
    const mealsTotal = deliveryCart.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);
    return mealsTotal;
  };

  const placeOrder = async (orderData) => {
    try {
      const res = await axios.post(`${API}/orders`, orderData, {
        withCredentials: true
      });

      if (res.data.success) {
        setRecentOrder({
          ...res.data.order,
          order_number: res.data.order_number,
          vendor_name: selectedVendor?.name || selectedVendor?.business_name,
          total_amount: res.data.order.total
        });
        setShowCheckoutModal(false);
        setShowCartModal(false);
        setShowOrderConfirmation(true);
        setDeliveryCart([]);
        toast.success(t('nutritionDeep.orderPlaced'));
        showCelebration('order_placed');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to place order';
      toast.error(errorMsg);
      throw error; // Re-throw to let CheckoutModal handle loading state
    }
  };

  // New function to handle "Order This Meal" click
  const handleOrderMealClick = async (meal, mealType) => {
    // Check if user has entered a usable postcode/ZIP
    if (!isPostcodeSearchable(userZipCode)) {
      toast.error(t('nutritionDeep.enterZipFirst'));
      return;
    }

    // Find vendors that offer this meal's cuisine
    const mealCuisine = meal.cuisine;
    const vendorsOfferingMeal = availableVendors.filter(vendor =>
      vendor.cuisines_offered.includes(mealCuisine)
    );

    if (vendorsOfferingMeal.length === 0) {
      toast.info(t('nutritionDeep.mealNotAvailable'));
      return;
    }

    // Fetch pricing and details for this meal from each vendor
    try {
      const vendorDetailsPromises = vendorsOfferingMeal.map(async (vendor) => {
        try {
          const res = await axios.get(
            `${API}/delivery/available-meals?vendor_id=${vendor.id}&meal_type=${mealType}`,
            { withCredentials: true }
          );

          // Find this specific meal in the vendor's offerings
          const mealWithPrice = res.data.meals?.find(m => m.name === meal.name);
          
          if (mealWithPrice) {
            return {
              ...vendor,
              meal_price: mealWithPrice.price,
              distance: calculateDistance(userZipCode), // Mock distance calculation
              available: true
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching meal from vendor ${vendor.id}:`, error);
          return null;
        }
      });

      const vendorDetails = (await Promise.all(vendorDetailsPromises)).filter(Boolean);

      if (vendorDetails.length === 0) {
        toast.info(t('nutritionDeep.mealUnavailable'));
        return;
      }

      // Sort by rating and delivery time
      vendorDetails.sort((a, b) => {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return a.avg_delivery_time - b.avg_delivery_time;
      });

      setSelectedMealForOrder({ ...meal, meal_type: mealType });
      setVendorsForMeal(vendorDetails);
      setShowVendorSelectionModal(true);
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      toast.error(t('nutritionDeep.vendorOptionsFail'));
    }
  };

  // Mock distance calculation (in production, use geolocation API)
  const calculateDistance = (zipCode) => {
    // Mock distances between 0.5 - 3.5 miles
    const distances = [0.8, 1.2, 1.5, 2.1, 2.8, 3.2];
    return distances[Math.floor(Math.random() * distances.length)];
  };

  // Add meal to cart from vendor selection modal
  const addMealFromVendor = (meal, vendor) => {
    // Set the selected vendor if not already set or different
    if (!selectedVendor || selectedVendor.id !== vendor.id) {
      setSelectedVendor(vendor);
      // Clear cart if switching vendors
      if (selectedVendor && selectedVendor.id !== vendor.id) {
        setDeliveryCart([]);
        toast.info(`Switched to ${vendor.name}. Previous cart cleared.`);
      }
    }

    const cartItem = {
      ...meal,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      price: vendor.meal_price,
      quantity: 1,
      subtotal: vendor.meal_price * 1  // price * quantity
    };

    setDeliveryCart(prev => {
      const existing = prev.find(item => item.name === meal.name);
      if (existing) {
        return prev.map(item =>
          item.name === meal.name
            ? { ...item, quantity: item.quantity + 1, subtotal: item.price * (item.quantity + 1) }
            : item
        );
      }
      return [...prev, cartItem];
    });

    setShowVendorSelectionModal(false);
    toast.success(`Added ${meal.name} to cart from ${vendor.name}`);
    showSupportMessage('cart_add');
  };

  // Check if meal is available for delivery
  const isMealAvailableForDelivery = (meal) => {
    if (!isPostcodeSearchable(userZipCode) || availableVendors.length === 0) {
      return false;
    }
    
    const mealCuisine = meal.cuisine;
    return availableVendors.some(vendor =>
      vendor.cuisines_offered.includes(mealCuisine)
    );
  };


  const handleSaveToFavorites = async (mealType, meal) => {
    try {
      const response = await axios.post(`${API}/nutrition/favorites`, {
        meal_data: meal,
        meal_type: mealType,
        tags: meal.tags || []
      }, { withCredentials: true });
      
      // Show emotional support message from backend
      if (response.data.support_message) {
        showCelebration(); // Use celebration style
        setTimeout(() => {
          showSupportMessage(response.data.support_message, 'celebration');
        }, 500);
      } else {
        toast.success(`${meal.name} saved to favorites! ⭐`);
      }
      
      fetchFavorites(); // Refresh favorites
      
      // Track favoriting
      try {
        await axios.post(`${API}/nutrition/track-interaction`, {
          meal_name: meal.name,
          meal_type: mealType,
          cuisine: meal.cuisine || '',
          interaction_type: 'favorited',
          meal_data: meal
        }, { withCredentials: true });
      } catch (error) {
        console.error('Error tracking favorite:', error);
      }
    } catch (error) {
      console.error('Error saving favorite:', error);
      toast.error(t('nutritionDeep.favoriteSaveFail'));
    }
  };

  const handleRemoveFavorite = async (favoriteId) => {
    try {
      await axios.delete(`${API}/nutrition/favorites/${favoriteId}`, { withCredentials: true });
      toast.success(t('nutritionDeep.removedFromFavorites'));
      fetchFavorites();
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error(t('nutritionDeep.favoriteRemoveFail'));
    }
  };

  const MealCard = ({ meal, mealType, onSelect, onSave, isSelected }) => {
    const whyThisMeal = meal.why_this_meal || "Balanced and nutritious meal";
    const isAvailable = isMealAvailableForDelivery(meal);

    // Culturally specific origin label & region-based badge style.
    const originLabel = getMealOriginLabel(meal);
    const originBadgeStyle = getMealOriginBadgeStyle(meal);

    return (
      <Card className={`transition-all hover:shadow-lg ${isSelected ? 'border-2 border-green-500 bg-green-50' : 'border-2 border-gray-200'}`}>
        <CardHeader className="pb-3">
          {/* Culturally specific origin badge — no broad regional labels. */}
          {originLabel && (
            <div className="mb-3">
              <Badge className={`${originBadgeStyle} border font-medium text-xs px-2.5 py-0.5`} data-testid="meal-origin-badge">
                {originLabel}
              </Badge>
            </div>
          )}
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-xl font-bold text-gray-900">{meal.name}</CardTitle>
              <CardDescription className="text-sm mt-2 text-gray-600">{meal.description}</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSave(mealType, meal)}
              className="ml-2 hover:bg-red-50"
              title="Save to favorites"
            >
              <Heart className="h-5 w-5 text-red-500 hover:fill-red-500" />
            </Button>
          </div>

          {/* Why This Meal - Shown for ALL meals equally */}
          <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center">
              <Info className="h-3 w-3 mr-1" />
              {t('nutritionDeep.whyThisMeal')}
            </p>
            <p className="text-xs text-blue-800 leading-relaxed">{whyThisMeal}</p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Nutrition Info - More prominent */}
            <div className="grid grid-cols-3 gap-3 bg-gray-50 p-3 rounded-lg">
              <div className="flex flex-col items-center">
                <Flame className="h-5 w-5 text-orange-500 mb-1" />
                <span className="text-sm font-semibold">{meal.calories}</span>
                <span className="text-xs text-gray-600">calories</span>
              </div>
              <div className="flex flex-col items-center">
                <Drumstick className="h-5 w-5 text-red-500 mb-1" />
                <span className="text-sm font-semibold">{meal.protein}g</span>
                <span className="text-xs text-gray-600">protein</span>
              </div>
              <div className="flex flex-col items-center">
                <Leaf className="h-5 w-5 text-green-500 mb-1" />
                <span className="text-sm font-semibold">{meal.carbs}g</span>
                <span className="text-xs text-gray-600">carbs</span>
              </div>
            </div>

            {/* Benefits */}
            {meal.benefits && meal.benefits.length > 0 && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                <p className="text-xs text-green-800 font-semibold mb-2">Health Benefits:</p>
                <ul className="text-xs text-green-700 space-y-1">
                  {meal.benefits.slice(0, 2).map((benefit, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-600 mr-1">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Select Button - More prominent */}
            <Button
              onClick={() => onSelect(mealType, meal)}
              className="w-full h-11 text-base font-semibold"
              variant={isSelected ? "default" : "outline"}
            >
              {isSelected ? (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  Selected for {mealType}
                </>
              ) : (
                <>
                  {t('nutritionDeep.selectThisMeal')}
                </>
              )}
            </Button>

            {/* Swap Button - Only show for selected meals */}
            {isSelected && (
              <Button
                onClick={() => handleSwapMeal(mealType, meal)}
                variant="outline"
                className="w-full h-11 text-base font-semibold border-healthloop-mint-300 text-healthloop-mint-700 hover:bg-healthloop-mint-50"
              >
                <RefreshCw className="mr-2 h-5 w-5" />
                {t('nutritionDeep.swapMeal')}
              </Button>
            )}

            {/* Order This Meal Button - Always visible when delivery enabled */}
            {deliveryEnabled && (
              <Button
                onClick={() => handleOrderMealClick(meal, mealType)}
                className={`w-full h-11 text-base font-semibold ${
                  isAvailable
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!isAvailable}
              >
                {isAvailable ? (
                  <>
                    <Truck className="mr-2 h-5 w-5" />
                    {t('nutritionDeep.orderThisMeal')}
                  </>
                ) : (
                  <>
                    <MapPin className="mr-2 h-5 w-5" />
                    {t('nutritionDeep.notAvailableNearYou')}
                  </>
                )}
              </Button>
            )}

            {/* Quick Feedback */}
            <div className="pt-3 border-t border-gray-200">
              <QuickFeedback 
                itemId={`${mealType}-${meal.name.replace(/\s+/g, '-').toLowerCase()}`}
                page="nutrition"
                itemType="meal_recommendation"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const MealSection = ({ title, mealType, options, icon: Icon }) => {
    const selected = selectedMeals[mealType];
    const isSwapping = swapping[mealType];
    
    // Group meals by cuisine for better organization
    const groupedByCuisine = options.reduce((acc, meal) => {
      const cuisine = meal.cuisine || 'other';
      if (!acc[cuisine]) acc[cuisine] = [];
      acc[cuisine].push(meal);
      return acc;
    }, {});
    
    const cuisineCount = Object.keys(groupedByCuisine).length;

    return (
      <div className="space-y-3">
        {/* Compact Section Header — slimmer than the original 2xl hero
            so the Meal Guidance area feels like grouped content rather
            than a Dashboard-style banner. */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-healthloop-mint-600 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 leading-tight truncate">{title}</h3>
              <p className="text-[11px] text-gray-500">
                {options.length} {options.length === 1 ? 'option' : 'options'}
                {cuisineCount > 0 && ` · ${cuisineCount} ${cuisineCount === 1 ? 'cuisine' : 'cuisines'}`}
              </p>
            </div>
            {selected && (
              <Badge className="ml-1 bg-green-600 text-white text-[10px] px-1.5 py-0">
                <Check className="h-2.5 w-2.5 mr-0.5" />
                Selected
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSwapMeals(mealType)}
            disabled={isSwapping}
            className="h-8 px-2.5 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${isSwapping ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline text-xs">{isSwapping ? 'Loading…' : 'Swap'}</span>
          </Button>
        </div>

        {/* Meal Options Grid - Sorted by cuisine */}
        {options.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedByCuisine).map(([cuisine, meals]) => (
              <div key={cuisine}>
                {/* Cuisine Group Label - Only show if multiple cuisines */}
                {cuisineCount > 1 && (
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {getMealOriginLabel({ cuisine }).replace(/ cuisines$/i, '')} · {meals.length} option{meals.length !== 1 ? 's' : ''}
                    </h4>
                  </div>
                )}
                
                {/* Meal Cards for this cuisine */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {meals.map((meal, idx) => (
                    <MealCard
                      key={`${cuisine}-${idx}`}
                      meal={meal}
                      mealType={mealType}
                      onSelect={handleSelectMeal}
                      onSave={handleSaveToFavorites}
                      isSelected={selected?.name === meal.name}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Salad className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600 font-medium">{t('nutritionDeep.noMealOptions')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('nutritionDeep.tryAdjustingPrefs')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const MyPlanSummary = () => {
    const totalCalories = Object.values(selectedMeals).reduce((sum, meal) => 
      meal ? sum + (meal.calories || 0) : sum, 0
    );
    const totalProtein = Object.values(selectedMeals).reduce((sum, meal) => 
      meal ? sum + (meal.protein || 0) : sum, 0
    );

    return (
      <Card className="sticky top-4 bg-gradient-to-br from-healthloop-mint-50 to-teal-50 border-2 border-healthloop-mint-200">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Salad className="h-5 w-5 mr-2 text-green-600" />
            {t('nutritionDeep.myMealPlanSummary')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            {Object.entries(selectedMeals).map(([type, meal]) => (
              <div key={type} className="flex items-center justify-between text-sm">
                <span className="font-medium capitalize">{type}:</span>
                <span className="text-gray-700">
                  {meal ? meal.name : 'Not selected'}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between font-semibold">
              <span>Total Calories:</span>
              <span>{totalCalories} cal</span>
            </div>
            <div className="flex items-center justify-between font-semibold">
              <span>Total Protein:</span>
              <span>{totalProtein}g</span>
            </div>
          </div>

          {Object.values(selectedMeals).every(meal => meal) && (
            <Button className="w-full" variant="default">
              <Sparkles className="mr-2 h-4 w-4" />
              {t('nutritionDeep.saveCompletePlan')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-healthloop-mint-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('nutritionDeep.loadingOptions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Sticky compact header — visually distinct from Dashboard's
          gradient hero. Single-row layout, smaller typography, with a
          subtle "preferences" chip so users see their active cuisine
          filters without a separate big card. */}
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10" data-testid="nutrition-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            aria-label={t('profile.backToDashboard')}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="nutrition-back-btn"
          >
            <ArrowLeft className="h-4 w-4 text-gray-600" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight flex items-center gap-2">
              <Salad className="h-4 w-4 text-healthloop-mint-600" />
              {t('nutrition.smartNutrition')}
            </h1>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate">
              {foodCategories.length > 0
                ? `${t('nutritionDeep.showingMealsFrom')}: ${foodCategories.map(c => availableCuisines.find(a => a.value === c)?.label).filter(Boolean).join(', ')}`
                : t('nutrition.subtitleAdaptive')}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              onClick={() => setShowPreferencesModal(true)}
              size="sm"
              variant="outline"
              className="h-8 px-2.5"
              data-testid="nutrition-customize-btn"
            >
              <Settings className="h-3.5 w-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">{t('nutritionDeep.editPreferences')}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFavorites(!showFavorites)}
              className="h-8 px-2.5"
              data-testid="nutrition-favorites-btn"
            >
              <Star className={`h-3.5 w-3.5 ${showFavorites ? 'fill-amber-400 text-amber-500' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 space-y-7">

        {/* ========== SECTION 1: DAILY NUTRITION SNAPSHOT ==========
            Compact at-a-glance stats. Visual-only placeholders (no
            new APIs) using the user's existing meal selection state
            to give a "what's set for today" signal. */}
        <section data-testid="nutrition-section-snapshot">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {t('nutritionDeep.sectionSnapshot')}
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">{t('nutritionDeep.sectionSnapshotSubtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <Card className="border border-gray-100 rounded-xl shadow-sm" data-testid="snapshot-hydration">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-sky-100 text-sky-700">
                    <Drumstick className="h-3.5 w-3.5 hidden" />
                    <Salad className="h-3.5 w-3.5 hidden" />
                    <Heart className="h-3.5 w-3.5 hidden" />
                    <Sparkles className="h-3.5 w-3.5 hidden" />
                    <Leaf className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">{t('nutritionDeep.snapshotHydration')}</span>
                </div>
                <p className="text-xs text-gray-700">{t('nutritionDeep.snapshotHydrationHint')}</p>
              </CardContent>
            </Card>
            <Card className="border border-gray-100 rounded-xl shadow-sm" data-testid="snapshot-calories">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-rose-100 text-rose-700">
                    <Flame className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">{t('nutritionDeep.snapshotCalories')}</span>
                </div>
                <p className="text-xs text-gray-700">{t('nutritionDeep.snapshotCaloriesHint')}</p>
              </CardContent>
            </Card>
            <Card className="border border-gray-100 rounded-xl shadow-sm" data-testid="snapshot-balance">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">{t('nutritionDeep.snapshotBalance')}</span>
                </div>
                <p className="text-xs text-gray-700">
                  {Object.values(selectedMeals).filter(Boolean).length}/3 {t('nutritionDeep.mealsLogged')}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gray-100 rounded-xl shadow-sm" data-testid="snapshot-tip">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-amber-100 text-amber-700">
                    <Sparkles className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">{t('nutritionDeep.snapshotTip')}</span>
                </div>
                <p className="text-xs text-gray-700 leading-snug">{t('nutritionDeep.snapshotTipHint')}</p>
              </CardContent>
            </Card>
          </div>
        </section>


        {/* ========== SECTION 2: SMART RECOMMENDATIONS ==========
            Local ecosystem discovery, healthy delivery, and culturally-
            aware ingredient sourcing. Reuses the existing Local Healthy
            Options card — relabelled as a clearly grouped section. */}
        <section data-testid="nutrition-section-recommendations">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {t('nutritionDeep.sectionRecommendations')}
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">{t('nutritionDeep.sectionRecommendationsSubtitle')}</p>
            </div>
          </div>

        {/* Healthy Shopping Guidance — purely educational, no postcode
            lookup, no vendor matching, no marketplace.

            2026-05-24 (MVP-safety): the postcode input + Search button
            were removed because they hit the (incomplete) /api/delivery/vendors
            backend and reported "no certified vendors available in
            MK10 7AJ" while the rest of the page still rendered generic
            "nearby" copy — which misled users into thinking the page did
            live vendor discovery. Until a real partner directory is in
            place, this section is presented as educational wellness
            guidance only. */}
        <Card className="mb-0 border border-emerald-100 bg-white" data-testid="local-options-card">
          <CardHeader>
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="flex items-center text-lg">
                  <Leaf className="h-5 w-5 mr-2 text-emerald-600" />
                  {t('nutritionDeep.culturalNutritionTitle')}
                </CardTitle>
                <CardDescription className="mt-1">
                  {t('nutritionDeep.culturalNutritionSubtitle')}
                </CardDescription>
              </div>
              {/* "Coming soon" pill — preserves the architectural slot for
                  real geolocation-based wellness discovery (Google Places
                  or similar) without misleading testers tonight. */}
              <span
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700"
                data-testid="nearby-discovery-coming-soon-badge"
              >
                <MapPin className="h-3 w-3" aria-hidden="true" />
                {t('nutritionDeep.nearbyDiscoveryComingSoon')}
              </span>
            </div>

            <div className="space-y-4 pt-4 border-t border-emerald-100">
              {/* Compliance-safe scope banner — sets expectations
                  immediately so users don't think the page does live
                  vendor matching. */}
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
                data-testid="shopping-guidance-scope"
              >
                {t('nutritionDeep.shoppingGuidanceScope')}
              </div>

              {/* Always-visible educational content (no postcode gating,
                  no vendor lookup, no "nearby" wording). */}
              {(
                <div
                  className="bg-emerald-50/40 border border-emerald-100 rounded-lg p-4 space-y-5"
                  data-testid="ingredient-tips-panel"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900 flex items-center mb-1">
                      <Leaf className="h-4 w-4 mr-2 text-emerald-600" />
                      {t('nutritionDeep.popularWellnessTitle')}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t('nutritionDeep.popularWellnessIntro')}
                    </p>
                  </div>

                  {/* Section A — Popular wellness foods (universal) */}
                  <div data-testid="wellness-section-popular-foods">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                      {t('nutritionDeep.subsectionPopularFoodsTitle')}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                      {POPULAR_WELLNESS_FOODS.map((food) => (
                        <li key={food} className="flex items-start gap-1.5 text-xs text-gray-700">
                          <Check className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Section B — Cultural ingredient suggestions */}
                  <div data-testid="wellness-section-cultural-ingredients">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                      {t('nutritionDeep.subsectionCulturalIngredientsTitle')}
                    </p>
                    <div className="space-y-2.5">
                      {getActiveCuisines().slice(0, 3).map((cuisineKey) => {
                        const hint = CULTURAL_INGREDIENT_HINTS[cuisineKey];
                        if (!hint) return null;
                        const label =
                          availableCuisines.find((c) => c.value === cuisineKey)?.label ||
                          cuisineKey;
                        return (
                          <div key={cuisineKey} className="text-xs text-gray-700">
                            <p className="font-medium text-gray-800">{label}</p>
                            <p className="text-gray-600 leading-relaxed">
                              <span className="text-gray-500">{t('nutritionDeep.lookFor')}:</span>{' '}
                              {hint.staples}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section C — Healthy swap ideas */}
                  <div data-testid="wellness-section-healthy-swaps">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                      {t('nutritionDeep.subsectionHealthySwapsTitle')}
                    </p>
                    <ul className="space-y-1.5">
                      {HEALTHY_SWAP_IDEAS.map((s) => (
                        <li key={s.from} className="text-xs text-gray-700 leading-relaxed">
                          <span className="text-gray-500">{s.from}</span>
                          <span className="mx-1.5 text-emerald-600">→</span>
                          <span className="text-gray-800">{s.to}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Section D — Local wellness environment */}
                  <div data-testid="wellness-section-local-environment">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                      {t('nutritionDeep.subsectionLocalEnvironmentTitle')}
                    </p>
                    <p className="text-[11px] text-gray-500 mb-2">
                      {t('nutritionDeep.subsectionLocalEnvironmentIntro')}
                    </p>
                    <ul className="space-y-1.5">
                      {[...new Set(
                        getActiveCuisines()
                          .flatMap((k) => LOCAL_WELLNESS_ENVIRONMENTS[k] || [])
                      )].slice(0, 6).map((env) => (
                        <li key={env} className="flex items-start gap-1.5 text-xs text-gray-700">
                          <MapPin className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                          <span>{env}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Section E — Healthy Stores & Wellness Markets Nearby.
                      INFORMATIONAL ONLY. Static, culturally inclusive
                      list of *types* of stores a user can look for —
                      no commerce, no live inventory, no booking, no
                      pricing, no delivery, no marketplace flow. */}
                  <div data-testid="wellness-section-healthy-stores">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                      <Store className="h-3.5 w-3.5 text-emerald-600" />
                      {t('nutritionDeep.healthyStoresTitle')}
                    </p>
                    <p className="text-[11px] text-gray-500 mb-3">
                      {t('nutritionDeep.healthyStoresIntro')}
                    </p>
                    <ul
                      className="space-y-2"
                      data-testid="wellness-section-healthy-stores-list"
                    >
                      {HEALTHY_STORE_TYPES.map((store) => {
                        const localised = t(`nutritionDeep.healthyStores.${store.key}`, {});
                        const type = localised?.type || store.type;
                        const description = localised?.description || store.description;
                        const tags = Array.isArray(localised?.tags) && localised.tags.length ? localised.tags : store.tags;
                        return (
                          <li
                            key={store.key}
                            className="rounded-lg border border-emerald-100 bg-white px-3 py-2"
                            data-testid={`healthy-store-${store.key}`}
                          >
                            <p className="text-xs font-medium text-gray-800">{type}</p>
                            <p className="text-[11px] text-gray-600 leading-relaxed mt-0.5">
                              {description}
                            </p>
                            {Array.isArray(tags) && tags.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>

                    {/* General healthy-shopping guidance — universal,
                        educational, no brands or products. */}
                    <div
                      className="mt-3 rounded-lg bg-white border border-emerald-100 px-3 py-2"
                      data-testid="healthy-shopping-tips"
                    >
                      <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1.5">
                        {t('nutritionDeep.healthyStoresGuidanceTitle')}
                      </p>
                      <ul className="space-y-1">
                        {(() => {
                          const tips = t('nutritionDeep.healthyShoppingTips', []);
                          const list = Array.isArray(tips) && tips.length ? tips : HEALTHY_SHOPPING_TIPS;
                          return list.map((tip) => (
                            <li key={tip} className="flex items-start gap-1.5 text-[11px] text-gray-700 leading-relaxed">
                              <Check className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ));
                        })()}
                      </ul>
                    </div>

                    {/* Compliance-safe disclaimer */}
                    <p
                      className="text-[10px] text-gray-500 italic mt-3"
                      data-testid="healthy-stores-disclaimer"
                    >
                      {t('nutritionDeep.healthyStoresDisclaimer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>
        </section>

        {/* ========== SECTION 2.5: CULTURAL MEAL INSPIRATION ==========
            User-controlled discovery: continent → country → (optional)
            tribe → (optional) wellness tag. Meals are HIDDEN until the
            user explicitly picks something OR taps "Browse All". This is
            an intentional UX choice to remove any feeling of automated
            recommendation. No AI, no backend, no commerce.

            Filters cascade off whatever is selected; clicking the active
            chip on any row clears that level. Available countries / tribes
            are computed live from the CULTURAL_MEAL_INSPIRATION array, so
            simply appending new meals automatically expands the explorer. */}
        <section data-testid="nutrition-section-cultural-meals">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Cultural Meal Inspiration
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Choose a continent or tap Browse All — for ideas, not delivery.
              </p>
            </div>
            <Badge className="bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 border border-emerald-100 font-medium">
              Educational
            </Badge>
          </div>

          {/* Continent row + Browse All. Single-select. Tapping the active
              continent clears the cascade. Continents without data are
              rendered disabled with a faint "coming soon" cue. */}
          <div
            className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 mb-2 scrollbar-hide"
            role="toolbar"
            aria-label="Continent selector"
            data-testid="cultural-meals-continent-row"
          >
            <button
              type="button"
              onClick={() => {
                setBrowseAllMeals(true);
                setSelectedContinent(null);
                setSelectedCountry(null);
                setSelectedTribe(null);
              }}
              className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                browseAllMeals
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'
              }`}
              data-testid="cultural-meals-browse-all"
            >
              Browse all
            </button>
            {CONTINENTS.map((c) => {
              const active = selectedContinent === c.name;
              return (
                <button
                  key={c.name}
                  type="button"
                  disabled={!c.hasData}
                  onClick={() => {
                    setBrowseAllMeals(false);
                    setSelectedContinent(active ? null : c.name);
                    setSelectedCountry(null);
                    setSelectedTribe(null);
                  }}
                  className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : c.hasData
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'
                        : 'bg-white text-gray-400 border-gray-200 cursor-not-allowed'
                  }`}
                  aria-pressed={active}
                  title={c.hasData ? `Explore ${c.name}` : `${c.name} — more cultural meals coming soon`}
                  data-testid={`cultural-meals-continent-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c.name}
                  {!c.hasData && <span className="ml-1 text-[9px] opacity-70">· soon</span>}
                </button>
              );
            })}
          </div>

          {/* Country row — appears only once a continent has been picked.
              Built dynamically from the meals belonging to that continent. */}
          {selectedContinent && (() => {
            const countries = Array.from(new Set(
              CULTURAL_MEAL_INSPIRATION
                .filter((m) => m.continent === selectedContinent)
                .map((m) => m.origin),
            ));
            if (countries.length === 0) return null;
            return (
              <div
                className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 mb-2 scrollbar-hide"
                role="toolbar"
                aria-label="Country selector"
                data-testid="cultural-meals-country-row"
              >
                {countries.map((country) => {
                  const active = selectedCountry === country;
                  return (
                    <button
                      key={country}
                      type="button"
                      onClick={() => {
                        setSelectedCountry(active ? null : country);
                        setSelectedTribe(null);
                      }}
                      className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                        active
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-teal-50 text-teal-700 border-teal-100 hover:bg-teal-100'
                      }`}
                      aria-pressed={active}
                      data-testid={`cultural-meals-country-${country.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {country}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Tribe / cultural-category row — appears only once a country
              has been picked AND there are multiple tribes to choose from.
              Single-tribe countries don't need the extra tap. */}
          {selectedCountry && (() => {
            const tribes = Array.from(new Set(
              CULTURAL_MEAL_INSPIRATION
                .filter((m) => m.origin === selectedCountry && m.tribe)
                .map((m) => m.tribe),
            ));
            if (tribes.length < 2) return null;
            return (
              <div
                className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 mb-2 scrollbar-hide"
                role="toolbar"
                aria-label="Tribe selector"
                data-testid="cultural-meals-tribe-row"
              >
                {tribes.map((tribe) => {
                  const active = selectedTribe === tribe;
                  return (
                    <button
                      key={tribe}
                      type="button"
                      onClick={() => setSelectedTribe(active ? null : tribe)}
                      className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                        active
                          ? 'bg-amber-600 text-white border-amber-600'
                          : 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100'
                      }`}
                      aria-pressed={active}
                      data-testid={`cultural-meals-tribe-${tribe.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`}
                    >
                      {tribe}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Wellness-Support tag filter — kept as an optional secondary
              filter applied AFTER geography narrows the results. Only
              rendered once the user has made a selection so the screen
              stays calm in the initial empty state. */}
          {(browseAllMeals || selectedContinent) && (
            <div
              className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 mb-2 scrollbar-hide"
              role="toolbar"
              aria-label="Wellness Support filters"
              data-testid="cultural-meals-filter-row"
            >
              <button
                type="button"
                onClick={() => setActiveMealTag(null)}
                className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                  activeMealTag === null
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'
                }`}
                data-testid="cultural-meals-filter-all"
              >
                All wellness tags
              </button>
              {WELLNESS_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveMealTag(activeMealTag === tag ? null : tag)}
                  className={`text-[11px] font-medium px-3 py-1 rounded-full border whitespace-nowrap transition-colors ${
                    activeMealTag === tag
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'
                  }`}
                  aria-pressed={activeMealTag === tag}
                  data-testid={`cultural-meals-filter-${tag.toLowerCase()}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Compliance-safe disclaimer (always visible inside the section). */}
          <p
            className="text-[10px] text-gray-500 italic mb-3"
            data-testid="cultural-meals-disclaimer"
          >
            HealthLoop provides preventive wellness and nutrition education only — not medical advice.
          </p>

          {/* Results area. Three modes:
                1) initial         → calm prompt, no cards
                2) continent picked but no meals (placeholder continent)
                                   → friendly "coming soon" message
                3) cards visible   → the existing carousel/grid */}
          {(() => {
            // Mode 1: nothing selected and not in browse-all mode
            if (!browseAllMeals && !selectedContinent) {
              return (
                <div
                  className="text-center rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 px-6 py-8"
                  data-testid="cultural-meals-prompt"
                >
                  <Sparkles className="h-5 w-5 text-emerald-600 mx-auto mb-1.5" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-800">
                    Pick a continent to explore — or tap Browse all
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Cultural meals appear only when you choose. No automatic suggestions.
                  </p>
                </div>
              );
            }

            // Compute visible meals based on the active cascade
            const visible = CULTURAL_MEAL_INSPIRATION.filter((m) => {
              if (selectedContinent && m.continent !== selectedContinent) return false;
              if (selectedCountry && m.origin !== selectedCountry) return false;
              if (selectedTribe && m.tribe !== selectedTribe) return false;
              if (activeMealTag && !(Array.isArray(m.tags) && m.tags.includes(activeMealTag))) return false;
              return true;
            });

            // Mode 2: continent (or filter) selected but no meals
            if (visible.length === 0) {
              const ctx = selectedTribe || selectedCountry || selectedContinent || 'this filter';
              return (
                <div
                  className="text-center rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 px-6 py-8"
                  data-testid="cultural-meals-empty"
                >
                  <Sparkles className="h-5 w-5 text-emerald-600 mx-auto mb-1.5" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-800">
                    More cultural meals for {ctx} coming soon
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    We're curating culturally respectful starter meals. Try another selection in the meantime.
                  </p>
                </div>
              );
            }

            // Mode 3: render the cards
            return (
              <div
                className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 pb-2 snap-x snap-mandatory sm:snap-none scrollbar-hide"
                data-testid="cultural-meals-carousel"
              >
                {visible.map((meal) => (
                  <Card
                    key={meal.name}
                    className="shrink-0 w-[260px] sm:w-auto snap-center sm:snap-align-none border border-emerald-100 bg-white rounded-2xl shadow-sm"
                    data-testid={`cultural-meal-card-${meal.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2 mb-1.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700">
                          <Salad className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-gray-900 leading-tight">{meal.name}</h3>
                          <p className="text-[11px] text-gray-500 truncate">
                            {meal.tribe ? `${meal.tribe} · ${meal.origin}` : meal.origin}
                            {meal.region ? ` · ${meal.region}` : ''}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed mb-2">{meal.description}</p>
                      {meal.ingredients && meal.ingredients.length > 0 && (
                        <p className="text-[11px] text-gray-500 mb-2">
                          <span className="font-medium text-gray-600">Common ingredients:</span>{' '}
                          {meal.ingredients.join(', ')}
                        </p>
                      )}
                      {meal.tags && meal.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {meal.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {meal.note && (
                        <p className="text-[11px] text-emerald-700 mt-2 italic">{meal.note}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })()}
        </section>

        {/* ========== SECTION 3: NUTRITION GOALS ==========
            Future-ready, inert placeholders that surface common goal
            tracks. Real personalisation flows will replace these as
            integrations land. Pharmacy/Medication concepts intentionally
            removed — they now live in the dedicated Care Services tab. */}
        <section data-testid="nutrition-section-goals">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {t('nutritionDeep.sectionGoals')}
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">{t('nutritionDeep.sectionGoalsSubtitle')}</p>
            </div>
            <Badge className="bg-gray-100 text-gray-500 text-[10px] px-2 py-0.5 border border-gray-200 font-medium">
              {t('nutritionDeep.comingSoon')}
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {[
              { icon: Heart,     title: t('nutritionDeep.goalWeight'),    desc: t('nutritionDeep.goalWeightDesc'),    accent: 'from-rose-100 to-pink-100 text-rose-700',          testid: 'goal-weight' },
              { icon: Flame,     title: t('nutritionDeep.goalEnergy'),    desc: t('nutritionDeep.goalEnergyDesc'),    accent: 'from-amber-100 to-orange-100 text-amber-700',      testid: 'goal-energy' },
              { icon: Sparkles,  title: t('nutritionDeep.goalImmunity'),  desc: t('nutritionDeep.goalImmunityDesc'),  accent: 'from-emerald-100 to-teal-100 text-emerald-700',    testid: 'goal-immunity' },
              { icon: Salad,     title: t('nutritionDeep.goalFamily'),    desc: t('nutritionDeep.goalFamilyDesc'),    accent: 'from-indigo-100 to-violet-100 text-indigo-700',    testid: 'goal-family' },
            ].map((g) => (
              <Card
                key={g.testid}
                className="border border-gray-100 rounded-xl shadow-sm bg-white opacity-95 cursor-not-allowed"
                aria-disabled="true"
                data-testid={g.testid}
              >
                <CardContent className="p-3">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br ${g.accent} shadow-sm mb-2`}>
                    <g.icon className="h-4 w-4" />
                  </span>
                  <p className="text-xs font-semibold text-gray-900 leading-tight">{g.title}</p>
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">{g.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>


        {/* Food Preferences Modal */}
        {showPreferencesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="text-2xl">{t('nutritionDeep.selectPreferences')}</CardTitle>
                <CardDescription>
                  Choose cuisines you'd like to see in your meal recommendations. You can select multiple.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableCuisines.map((cuisine) => (
                    <div
                      key={cuisine.value}
                      onClick={() => toggleCategorySelection(cuisine.value)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedCategories.includes(cuisine.value)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{cuisine.label}</h4>
                          <p className="text-xs text-gray-600 mt-1">{cuisine.description}</p>
                        </div>
                        {selectedCategories.includes(cuisine.value) && (
                          <Check className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {selectedCategories.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      ⚠️ Please select at least one cuisine to see personalized meal recommendations.
                    </p>
                  </div>
                )}

                {selectedCategories.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      ✓ {selectedCategories.length} cuisine{selectedCategories.length > 1 ? 's' : ''} selected. 
                      Meals will be filtered to show only these options.
                    </p>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <Button 
                    onClick={handleSavePreferences} 
                    className="flex-1"
                    disabled={selectedCategories.length === 0}
                  >
                    {t('nutritionDeep.savePreferences')}
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedCategories(foodCategories);
                      setShowPreferencesModal(false);
                      try { localStorage.setItem('nutritionPrefsPromptDismissed', '1'); } catch (_) {}
                    }} 
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ========== SECTION 4: MEAL GUIDANCE ==========
            The existing meal builder + favorites lives inside this
            grouped section so it's clearly the "meal-planning" surface
            of the page rather than a Dashboard-style content block. */}
        <section data-testid="nutrition-section-guidance">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {t('nutritionDeep.sectionGuidance')}
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">{t('nutritionDeep.sectionGuidanceSubtitle')}</p>
            </div>
          </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="meal-builder">{t('nutritionDeep.mealBuilder')}</TabsTrigger>
            <TabsTrigger value="favorites">{t('nutritionDeep.myFavorites')}</TabsTrigger>
          </TabsList>

          {/* Meal Builder Tab */}
          <TabsContent value="meal-builder">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Main Content - Meal Options */}
              <div className="lg:col-span-3 space-y-8">
                <MealSection
                  title="Breakfast Options"
                  mealType="breakfast"
                  options={breakfastOptions}
                  icon={Salad}
                />
                
                <MealSection
                  title="Lunch Options"
                  mealType="lunch"
                  options={lunchOptions}
                  icon={Drumstick}
                />
                
                <MealSection
                  title="Dinner Options"
                  mealType="dinner"
                  options={dinnerOptions}
                  icon={Flame}
                />
                
                <MealSection
                  title="Snack Options"
                  mealType="snacks"
                  options={snacksOptions}
                  icon={Leaf}
                />
              </div>

              {/* Sidebar - My Plan Summary */}
              <div className="lg:col-span-1">
                <MyPlanSummary />
              </div>
            </div>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('nutritionDeep.myFavoriteMeals')}</CardTitle>
                  <CardDescription>{t('nutritionDeep.quickAccessSavedMeals')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.keys(favorites).every(key => favorites[key].length === 0) ? (
                    <div className="text-center py-8 text-gray-500">
                      <Star className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{t('nutritionDeep.noFavoritesYet')}</p>
                      <p className="text-sm mt-2">{t('nutritionDeep.saveMealsFromBuilder')}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.entries(favorites).map(([mealType, meals]) => (
                        meals.length > 0 && (
                          <div key={mealType}>
                            <h4 className="font-semibold capitalize mb-3">{mealType}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {meals.map((fav) => (
                                <Card key={fav.id}>
                                  <CardHeader>
                                    <CardTitle className="text-base">{fav.meal_data.name}</CardTitle>
                                    <CardDescription className="text-xs">{fav.meal_data.description}</CardDescription>
                                  </CardHeader>
                                  <CardContent className="space-y-2">
                                    <div className="text-sm text-gray-600">
                                      {fav.meal_data.calories} cal | {fav.meal_data.protein}g protein
                                    </div>
                                    <div className="flex space-x-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => handleSelectMeal(mealType, fav.meal_data)}
                                      >
                                        Select
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleRemoveFavorite(fav.id)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        </section>

        {/* ========== FUTURE NUTRITION SUPPORT — preview teaser ==========
            Calm, non-commercial preview positioning HealthLoop as a
            preventive wellness ecosystem. No subscription UI, no
            payment surface — purely informational. */}
        <section
          className="rounded-2xl border border-healthloop-mint-100 bg-gradient-to-br from-healthloop-mint-50/60 via-white to-purple-50/40 px-5 py-5 sm:px-6"
          data-testid="nutrition-future-support"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-healthloop-mint-100 to-purple-100 text-healthloop-mint-700 shadow-sm shrink-0">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-gray-900 leading-tight">
                  {t('nutritionDeep.futureNutritionTitle')}
                </h2>
                <Badge className="bg-white text-gray-500 text-[10px] px-2 py-0.5 border border-gray-200 font-medium">
                  {t('careServices.futureFeatureBadge')}
                </Badge>
              </div>
              <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
                {t('nutritionDeep.futureNutritionBody')}
              </p>
            </div>
          </div>
        </section>
      </div>


      {/* Vendor Selection Modal - For specific meal */}
      {showVendorSelectionModal && selectedMealForOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center text-xl">
                    <Truck className="h-5 w-5 mr-2 text-green-600" />
                    Order: {selectedMealForOrder.name}
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {t('nutritionDeep.chooseVendor')}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowVendorSelectionModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Meal Preview */}
              <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">{selectedMealForOrder.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{selectedMealForOrder.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                        <span className="flex items-center">
                          <Flame className="h-4 w-4 mr-1 text-orange-500" />
                          {selectedMealForOrder.calories} cal
                        </span>
                        <span className="flex items-center">
                          <Drumstick className="h-4 w-4 mr-1 text-red-500" />
                          {selectedMealForOrder.protein}g protein
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {selectedMealForOrder.cuisine?.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Available Vendors */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">
                  Available from {vendorsForMeal.length} {vendorsForMeal.length === 1 ? 'vendor' : 'vendors'}
                </h4>
                
                {vendorsForMeal.map((vendor) => (
                  <Card
                    key={vendor.id}
                    className="cursor-pointer transition-all hover:shadow-md hover:border-green-300 border-2"
                    onClick={() => addMealFromVendor(selectedMealForOrder, vendor)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Vendor Name & Rating */}
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900 text-lg">{vendor.name}</h4>
                            <Badge variant="secondary" className="text-xs">
                              ⭐ {vendor.rating}
                            </Badge>
                          </div>
                          
                          {/* Vendor Description */}
                          <p className="text-sm text-gray-600 mb-3">{vendor.description}</p>
                          
                          {/* Key Details Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {/* Price */}
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">{t('nutritionDeep.price')}</span>
                              <span className="font-bold text-green-600 text-lg flex items-center">
                                <DollarSign className="h-4 w-4" />
                                {vendor.meal_price.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Delivery Time */}
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">{t('nutritionDeep.delivery')}</span>
                              <span className="font-semibold text-gray-900 flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-blue-500" />
                                {vendor.avg_delivery_time} min
                              </span>
                            </div>
                            
                            {/* Delivery Fee */}
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">{t('nutritionDeep.deliveryFee')}</span>
                              <span className="font-semibold text-gray-900">
                                ${vendor.delivery_fee.toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Distance */}
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">{t('nutritionDeep.distance')}</span>
                              <span className="font-semibold text-gray-900 flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-red-500" />
                                {vendor.distance.toFixed(1)} mi
                              </span>
                            </div>
                          </div>
                          
                          {/* Total Preview */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Total (meal + delivery)</span>
                              <span className="font-bold text-lg text-gray-900">
                                ${(vendor.meal_price + vendor.delivery_fee).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <Button
                        className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white h-11 text-base font-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          addMealFromVendor(selectedMealForOrder, vendor);
                        }}
                      >
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        Add to Cart from {vendor.name}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Helper Text */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                <p className="font-semibold mb-1">💡 Tip:</p>
                <p>Choose based on price, delivery time, or vendor rating. You can order multiple meals from the same vendor to save on delivery fees!</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cart Modal */}
      {showCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {t('nutritionDeep.yourCart')}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCartModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              {selectedVendor && (
                <CardDescription>
                  Ordering from {selectedVendor.name} • {selectedVendor.avg_delivery_time} min delivery
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {deliveryCart.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('nutritionDeep.cartEmpty')}</p>
                  <p className="text-sm mt-1">{t('nutritionDeep.addMealsToStart')}</p>
                </div>
              ) : (
                <>
                  {/* Cart Items */}
                  <div className="space-y-3">
                    {deliveryCart.map((item) => (
                      <Card key={item.name} className="border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{item.name}</h4>
                              <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                <span>{item.calories} cal</span>
                                <span>{item.protein}g protein</span>
                                <Badge variant="secondary" className="text-xs">
                                  {item.meal_type}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateCartQuantity(item.name, item.quantity - 1)}
                                >
                                  -
                                </Button>
                                <span className="text-sm font-medium px-3">{item.quantity}</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateCartQuantity(item.name, item.quantity + 1)}
                                >
                                  +
                                </Button>
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="font-semibold text-gray-900">
                                ${(item.price * item.quantity).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                ${item.price.toFixed(2)} each
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFromCart(item.name)}
                                className="mt-2 text-red-600 hover:text-red-700"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Order Summary */}
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('nutritionDeep.subtotal')}</span>
                      <span className="font-medium">${calculateCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('nutritionDeep.deliveryFee')}</span>
                      <span className="font-medium">${selectedVendor?.delivery_fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-3">
                      <span>{t('nutritionDeep.total')}</span>
                      <span>${(calculateCartTotal() + (selectedVendor?.delivery_fee || 0)).toFixed(2)}</span>
                    </div>

                    {/* Minimum Order Check */}
                    {calculateCartTotal() < selectedVendor?.min_order_amount && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                        Add ${(selectedVendor.min_order_amount - calculateCartTotal()).toFixed(2)} more to reach the ${selectedVendor.min_order_amount} minimum order
                      </div>
                    )}
                  </div>

                  {/* Delivery Details */}
                  <div className="space-y-3 border-t pt-4">
                  </div>

                  {/* Proceed to Checkout Button */}
                  <Button
                    onClick={() => {
                      if (deliveryCart.length === 0) {
                        toast.error(t('nutritionDeep.cartEmpty'));
                        return;
                      }
                      if (calculateCartTotal() < (selectedVendor?.min_order_amount || 0)) {
                        toast.error(`Minimum order amount is $${selectedVendor?.min_order_amount.toFixed(2)}`);
                        return;
                      }
                      setShowCartModal(false);
                      setShowCheckoutModal(true);
                    }}
                    className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
                    disabled={
                      calculateCartTotal() < (selectedVendor?.min_order_amount || 0) ||
                      deliveryCart.length === 0
                    }
                  >
                    <Truck className="mr-2 h-5 w-5" />
                    Proceed to Checkout - ${(calculateCartTotal() + (selectedVendor?.delivery_fee || 0)).toFixed(2)}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Order Confirmation Modal */}
      {showOrderConfirmation && recentOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Order Confirmed! 🎉</CardTitle>
              <CardDescription>
                {t('nutritionDeep.orderPlacedDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('nutritionDeep.orderNumber')}</span>
                  <span className="font-mono font-bold">{recentOrder.order_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('nutritionDeep.vendor')}</span>
                  <span className="font-medium">{recentOrder.vendor_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('nutritionDeep.totalAmount')}</span>
                  <span className="font-bold text-lg">${recentOrder.total_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('nutritionDeep.estimatedDelivery')}</span>
                  <span className="font-medium flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {new Date(recentOrder.estimated_delivery_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>What's Next?</strong>
                </p>
                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                  <li>• The vendor will prepare your meals</li>
                  <li>• You'll receive delivery updates via notifications</li>
                  <li>• Track your order status in the Orders page</li>
                  <li>• You can cancel within 5 minutes if needed</li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowOrderConfirmation(false);
                    setRecentOrder(null);
                  }}
                >
                  {t('nutritionDeep.continueShopping')}
                </Button>
                <Button
                  onClick={() => {
                    setShowOrderConfirmation(false);
                    setRecentOrder(null);
                    navigate('/orders');
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {t('nutritionDeep.trackOrder')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        cart={deliveryCart}
        vendor={selectedVendor}
        userProfile={user}
        onPlaceOrder={placeOrder}
      />

      {/* Meal Preferences Modal */}
      <MealPreferencesModal
        open={showPreferencesModal}
        onClose={() => {
          setShowPreferencesModal(false);
          try { localStorage.setItem('nutritionPrefsPromptDismissed', '1'); } catch (_) {}
        }}
        initialPreferences={userPreferences}
        onSave={handlePreferencesSave}
      />

      {/* Meal Swap Modal */}
      {showSwapModal && (
        <MealSwapModal
          open={showSwapModal}
          onClose={() => setShowSwapModal(false)}
          mealType={swapMealType}
          currentMeal={swapMealCurrent}
          allMeals={
            swapMealType === 'breakfast' ? breakfastOptions :
            swapMealType === 'lunch' ? lunchOptions :
            swapMealType === 'dinner' ? dinnerOptions :
            snacksOptions
          }
          userPreferences={userPreferences}
          onSwapComplete={handleSwapComplete}
        />
      )}

      <NeedHelpButton />
      <AppFooter />
    </div>
  );
};

export default Nutrition;
